import * as React from 'react';
import {
  useLocation,
  //  useNavigate
} from 'react-router';

import { Drawer } from '@/components';
import { Button } from '@/components/ui/button';
import { NotificationBanner } from '@/components/ui/notification-banner/notification-banner';
import { paths } from '@/config/paths';
import { BatchSelection } from '@/features/batch-selection';
import { BhanzuBuddy } from '@/features/bhanzu-buddy';
import { useProfilesData } from '@/features/user';
import { useBhanzuBuddyFeatures } from '@/lib/feature-flags';
import { useIsMobileOrTablet } from '@/lib/hooks/use-media-query';
import { useBatchSelectionDrawerStore } from '@/stores/batch-selection-drawer';
import { cn } from '@/utils';

import { Footer } from './components/footer/footer';
import { MainHeader, MobileHeader, MainHeaderProps } from './components/header';

export interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
  headerProps?: Omit<MainHeaderProps, 'className'>;
  headerClassName?: string;
}

const MainLayout = React.forwardRef<HTMLDivElement, MainLayoutProps>(
  ({ children, className, headerProps, headerClassName, ...props }, ref) => {
    const isMobileOrTablet = useIsMobileOrTablet();
    const { selectedProfile, onboardingCourses, isOnboardingCandidate } =
      useProfilesData();
    const isDrawerOpen = useBatchSelectionDrawerStore(
      state => state.isDrawerOpen
    );
    const setIsDrawerOpen = useBatchSelectionDrawerStore(
      state => state.setIsDrawerOpen
    );
    const { showBhanzuBuddy, enableQuickChallenge } = useBhanzuBuddyFeatures();
    const currentPathname = useLocation().pathname;

    const isAnyBatchSelectionPending = onboardingCourses?.find(
      course => !course.batch_id
    );

    const showOnboardingBanner = React.useMemo(() => {
      return (
        isOnboardingCandidate &&
        isAnyBatchSelectionPending &&
        currentPathname !== paths.app.onboarding.path
      );
    }, [isOnboardingCandidate, isAnyBatchSelectionPending, currentPathname]);

    // Get variant IDs from enrolled courses that need batch assignment
    const variantIds =
      onboardingCourses
        ?.filter(course => !course.batch_id || course.batch_id.trim() === '')
        ?.map(course => ({
          variantId: course.variant_id,
          courseName: course.course_name,
        })) ?? [];

    return (
      <>
        {/* Notification Banner */}
        {showOnboardingBanner ? (
          <>
            <NotificationBanner
              title={`Hey ${selectedProfile?.name}! Your ${isAnyBatchSelectionPending?.subject} Course batch selection is pending.`}
              variant="error"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  color="light"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Select Batch now
                </Button>
              }
            />
          </>
        ) : null}

        {/* Layout */}

        {/* Mobile/Tablet Header - Fixed outside container */}
        {isMobileOrTablet ? <MobileHeader {...headerProps} /> : null}

        <div
          ref={ref}
          className={cn(
            'min-h-screen bg-white flex flex-col max-w-(--max-width) mx-auto lg:px-16 md:px-8 px-4 mb-20',
            isMobileOrTablet ? 'pt-36' : '',
            className
          )}
          {...props}
        >
          {/* Desktop Header */}
          {isMobileOrTablet ? null : (
            <div className="relative lg:sticky top-0 z-(--z-header) w-full py-6">
              <MainHeader
                className={cn('shadow-sm', headerClassName)}
                {...headerProps}
              />
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 flex flex-col gap-4 pb-6">{children}</main>

          {/* Bhanzu Buddy - Only show when user has selected profile and feature is enabled */}
          {selectedProfile?.id && showBhanzuBuddy ? (
            <BhanzuBuddy
              leadId={selectedProfile.id}
              defaultMode="explain"
              enableQuickChallenge={enableQuickChallenge}
              onMessageSent={_response => {
                // Handle successful message
              }}
              onError={_error => {
                // Handle error
              }}
              placeholder="Ask me anything about your studies..."
            />
          ) : null}
        </div>

        <Drawer
          showCloseButton={true}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          borderRadius="pt-0 px-0 lg:px-0 bg-white"
          childClass="max-w-(100%)"
          innerContentClassName="p-0"
        >
          <BatchSelection
            profileId={selectedProfile?.id ?? ''}
            leadId={selectedProfile?.base_lead_id ?? ''}
            variantIds={variantIds}
            country={selectedProfile?.country ?? 'IN'}
            userEmail={selectedProfile?.communication_email_id ?? ''}
            selectedProfile={selectedProfile}
            onboardingCourses={onboardingCourses}
          />
        </Drawer>

        {isMobileOrTablet ? <Footer /> : null}
      </>
    );
  }
);

MainLayout.displayName = 'MainLayout';

export { MainLayout };
